import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchApi } from '../../utils/api';
import { useWebsiteInputs } from '../../utils/hooks/useWebsiteInputs';
import { getImageUrl } from '../../utils/supabaseStorage';
import Seo from '../common/seo';

// Sort people within a section by rank (unranked entries go last)
const sortByRank = (people) => {
  return [...people].sort((a, b) => {
    if (a.rank && b.rank) return a.rank - b.rank;
    if (a.rank && !b.rank) return -1;
    if (!a.rank && b.rank) return 1;
    return 0;
  });
};

function PersonBox({ person }) {
  return (
    <div className="people-box">
      {person.image && (
        <img
          src={person.image.startsWith('http') ? person.image : getImageUrl(person.image)}
          alt={person.name}
          className="people-image"
        />
      )}
      <div className="text-container">
        <div className="people-name">{person.name}</div>
        <div className="people-title">{person.title}</div>
        {(person.experience || []).map((exp, expIndex) => (
          <div key={expIndex} className="people-text">{exp}</div>
        ))}
      </div>
    </div>
  );
}

function People() {
  const { inputs: websiteInputs } = useWebsiteInputs();
  const [peopleData, setPeopleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPeople = async () => {
      try {
        setLoading(true);
        const response = await fetchApi('/api/public/website-inputs/', {
          method: 'GET',
          params: {
            name: 'people'
          }
        });

        if (response.data && response.data.length > 0) {
          setPeopleData(response.data[0].input_json);
        }
      } catch (err) {
        console.error('Error fetching people data:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchPeople();
  }, []);

  const totalInvestments = websiteInputs?.['Total Investments'];
  const teamData = sortByRank(peopleData?.team || []);
  const advisorData = sortByRank(peopleData?.advisors || []);

  if (loading) return <div className="empty-loading-container"></div>;

  return (
    <div className="container">
      <Seo path="/people" />
      <h1 className="page-title">PEOPLE</h1>

      {error && (
        <div className="content-section">
          <p className="people-text">Error loading people data</p>
        </div>
      )}

      {!error && (
        <>
          <div className="content-section">
            <h2>Leads</h2>
            <span className="people-text grey-text">Committed and trying to help</span>
            <div className="people-container">
              {teamData.map((member, index) => (
                <PersonBox key={index} person={member} />
              ))}
            </div>
          </div>

          <div className="content-section">
            <h2>Support</h2>
            <span className="people-text grey-text">Function experts who know what great looks like</span>
            <div className="people-container">
              {advisorData.map((advisor, index) => (
                <PersonBox key={index} person={advisor} />
              ))}
            </div>
          </div>
        </>
      )}

      <div className="content-section">
        <h2>LeapYear Network</h2>
        <span className="people-text grey-text">Our network is your network</span>
        <div className="network-blocks">
          <Link to="/investments" className="network-block network-block-link">
            <div className="network-block-title">LeapYear Founders</div>
            <div className="network-block-stat">{totalInvestments ? `${totalInvestments} teams so far` : 'Teams so far'}</div>
            <div className="network-block-text">See all teams we funded →</div>
          </Link>
          <Link to="/investors" className="network-block network-block-link">
            <div className="network-block-title">Investor Network</div>
            <div className="network-block-stat">Venture funds + angels</div>
            <div className="network-block-text">See the Investor Network →</div>
          </Link>
          <div className="network-block">
            <div className="network-block-title">Builder Network</div>
            <div className="network-block-logos">
              <img src={getImageUrl('company-logos/doordash.png')} alt="DoorDash" className="network-logo" />
              <img src={getImageUrl('company-logos/uber.png')} alt="Uber" className="network-logo" />
              <img src={getImageUrl('company-logos/twitch.png')} alt="Twitch" className="network-logo" />
              <span className="network-more-label">+ more</span>
            </div>
            <div className="network-block-text">And access to great operators</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default People;
